import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import sessionHandling from "services/utils/notificationUtils";
import { designWeights } from "services/constants/designTokens";
import { EmptyState } from "components/CustomComponents/EmptyState";
import { checkViewerPermission } from "services/utils/HelperFunction";
import { dashboard } from "./dashboardStyles";
import { formatShiftClock } from "../MyShift/shiftHelpers";

// Upcoming Schedule card. Reads the same `daily_user_shift_assignments`
// endpoint as My Zone > Shift (MyShift/index.js), scoped to the logged-in user
// for the next 7 days, and lays the day-by-day shifts out as a small timeline.
const RANGE_DAYS = 7;

// Derive the status chip from the shift row's own flags (same fields MyShift's
// Weekly view keys on: shift_name / is_weekoff), so the label matches what the
// Shift screen would show for the same day.
const statusFor = (shift, isToday) => {
  const isWeekoff = shift?.is_weekoff || shift?.shift_name === "Weekoff";
  if (isWeekoff) return { label: "Week Off", color: dashboard.colors.amber };
  if (!shift?.shift_name) return { label: "Not Joined", color: dashboard.colors.subText };
  if (isToday) return { label: "Working", color: dashboard.colors.green };
  return { label: "Scheduled", color: dashboard.colors.blue };
};

const relativeDay = (m) => {
  const today = moment().startOf("day");
  const diff = m.clone().startOf("day").diff(today, "days");
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return m.format("ddd");
};

export const UpcomingScheduleSection = () => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, token, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const history = useHistory();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  // This card previewed shift data and linked to the Shift screen for every
  // user, even one with no viewer access to "Shift" at all -- Home is meant
  // to surface what's relevant to the signed-in user, not every card
  // regardless of access.
  const hasAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "Shift",
    "Shift",
  );

  useEffect(() => {
    let cancelled = false;
    const fetchSchedule = async () => {
      setLoading(true);
      try {
        const from = moment().format("YYYY-MM-DD");
        const to = moment().add(RANGE_DAYS - 1, "days").format("YYYY-MM-DD");
        const response = await fetch(
          `${domain}daily_user_shift_assignments/?selected_user_id=${userDetails?.id}&from_date=${from}&to_date=${to}&company_id=${requestCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          // res.data is an array of per-user wrappers, each with a `shifts`
          // array; scoped to one user it is normally a single wrapper, but we
          // flatMap to be safe if the API ever returns more than one.
          const rows = Array.isArray(res?.data)
            ? res.data.flatMap((u) => u?.shifts || [])
            : [];
          if (!cancelled) setShifts(rows);
        } else if (response.status === 409) {
          sessionHandling();
        }
      } catch (error) {
        console.error("[UpcomingScheduleSection] operation failed:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (userDetails?.id && hasAccess) fetchSchedule();
    else setLoading(false);
    return () => {
      cancelled = true;
    };
  }, [domain, token, userDetails?.id, hasAccess, requestCompanyId]);

  if (!hasAccess) return null;

  const upcoming = shifts.slice(0, 5);

  return (
    <Box sx={dashboard.cardSx}>
      <Typography fontWeight={designWeights.heading} sx={{ color: dashboard.colors.text, mb: 1.5 }}>
        Upcoming Schedule
      </Typography>
      {loading ? (
        <CircularProgress size={22} />
      ) : upcoming.length === 0 ? (
        <EmptyState subtitle="No upcoming shifts scheduled." size={96} sx={{ py: 2 }} />
      ) : (
        <Box>
          {upcoming.map((shift, idx) => {
            const m = moment(shift.date, "DD-MM-YYYY");
            const isToday = m.isSame(moment(), "day");
            const status = statusFor(shift, isToday);
            const hasTime = shift.shift_start_time && shift.shift_end_time;
            return (
              <Stack
                key={`${shift.date}-${idx}`}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  py: 1.1,
                  borderBottom:
                    idx !== upcoming.length - 1 ? "1px solid #F0F1F4" : "none",
                }}
              >
                <Box sx={dashboard.iconWrap(status.color)}>
                  <CalendarMonthIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: dashboard.colors.subText }}>
                    {relativeDay(m)} • {m.format("ddd, DD MMM")}
                  </Typography>
                  <Typography fontWeight={designWeights.medium} sx={{ color: dashboard.colors.text }}>
                    {shift.shift_name || "Not Joined"}
                  </Typography>
                  {hasTime && (
                    <Typography variant="caption" sx={{ color: dashboard.colors.subText }}>
                      {formatShiftClock(shift.shift_start_time)}
                      {" - "}
                      {formatShiftClock(shift.shift_end_time)}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    px: 1.2,
                    py: 0.3,
                    borderRadius: 5,
                    fontSize: 11,
                    fontWeight: designWeights.semibold,
                    whiteSpace: "nowrap",
                    color: status.color,
                    bgcolor: `${status.color}1A`,
                  }}
                >
                  {status.label}
                </Box>
              </Stack>
            );
          })}
          <Typography
            sx={{ ...dashboard.linkSx, color: dashboard.colors.blue, mt: 1.5, textAlign: "center" }}
            onClick={() => history.push("/admin/myzone/myshift")}
          >
            View Full Calendar →
          </Typography>
        </Box>
      )}
    </Box>
  );
};
